import { AnnaError } from "@anna-money/anna-web-lib/utils";
import dayjs from "dayjs";

import { companyFormToCompanyUpdateData } from "helpers/forms/acn/company";
import type { CompanyFormValues } from "helpers/forms/acn/companyTypes";
import { AsicAgentSignatureStore } from "services/asicAgentNomination/AsicAgentSignatureStore";
import { CompanyStore } from "services/company/companyStore";
import { CompanyAddressStore } from "services/companyAddress/au/companyAddressStore";
import { CompanyProductsStore } from "services/companyProducts/companyProductsStore";
import {
  CompanyProduct,
  type DefaultCompanyProductDetailsData,
  RegisterBusinessNamePeriods,
} from "services/companyProducts/companyProductsTypes";
import { PricingPlanStore } from "services/pricingPlan/au/pricingPlanStore";
import { type PricingPlanAu } from "services/pricingPlan/pricingPlanTypes";

export enum CompanyAcnStepEntity {
  VirtualOffice = "VirtualOffice",
  VirtualPlanSelect = "VirtualPlanSelect",
  VirtualAddressSelect = "VirtualAddressSelect",
  BusinessName = "BusinessName",
  PricingPlan = "PricingPlan",
  Address = "Address",
  AsicAgentNomination = "AsicAgentNomination",
}

export class CompanyAcnStepStore {
  constructor(
    private readonly _companyStore: CompanyStore,
    private readonly _companyAddressStore: CompanyAddressStore,
    private readonly _companyProductsStore: CompanyProductsStore,
    private readonly _pricingPlanStore: PricingPlanStore,
    private readonly _asicAgentSignatureStore: AsicAgentSignatureStore,
  ) {}

  async updateEntity(data: CompanyFormValues, entity: CompanyAcnStepEntity): Promise<void> {
    switch (entity) {
      case CompanyAcnStepEntity.VirtualOffice:
        if (data.virtualOfficeRegister) {
          await this._companyAddressStore.updateRegisteredVirtualOffice(data.virtualOfficeRegister);
        }
        break;
      case CompanyAcnStepEntity.VirtualPlanSelect:
        await this._companyAddressStore.updateAddress({ virtualOfficeBillingPeriod: data.virtualOfficeBillingPeriod });
        break;
      case CompanyAcnStepEntity.VirtualAddressSelect:
        await this._companyAddressStore.updateAddress({
          address: data.address,
        });
        break;
      case CompanyAcnStepEntity.PricingPlan:
        await this._updatePricingPlan(data.pricingPlan);
        break;
      case CompanyAcnStepEntity.BusinessName:
        await this._updateBusinessName(data.businessName, data.businessNamePeriod);
        break;
      case CompanyAcnStepEntity.Address:
        await this._companyAddressStore.updateAddress(
          data.useVirtualOffice
            ? { virtualOfficeBillingPeriod: data.virtualOfficeBillingPeriod }
            : { address: data.address, useHomeAddressAsRegistered: data.useHomeAddressAsRegistered },
        );
        break;
      case CompanyAcnStepEntity.AsicAgentNomination: {
        if (typeof data.auAsicAgentNominationRequested === "undefined") {
          throw new AnnaError("No ASIC agent nomination value");
        }
        let asicAgentSigFileId = data.asicAgentSigFileId;
        // When nominating, the signature pad captures a data URL on submit; wrap
        // it in the rendered nomination statement, upload that as the evidence
        // image and use the resulting file id for the backend call.
        if (data.auAsicAgentNominationRequested && data.asicAgentSignature) {
          asicAgentSigFileId = await this._asicAgentSignatureStore.uploadNominationEvidence(data.asicAgentSignature, {
            companyName: this._companyName(),
            signedAtLabel: dayjs().format("D MMMM YYYY, h:mm A"),
          });
        }
        await this._companyStore.updateAsicAgentNomination(data.auAsicAgentNominationRequested, asicAgentSigFileId);
        break;
      }
      default:
        await this._companyStore.updateCompany(companyFormToCompanyUpdateData(data));
    }
  }

  private _companyName(): string | undefined {
    const company = this._companyStore.company;
    if (!company?.companyName) {
      return undefined;
    }
    return company.namePostfix ? `${company.companyName} ${company.namePostfix}` : company.companyName;
  }

  private async _updateBusinessName(name?: string, period?: RegisterBusinessNamePeriods): Promise<void> {
    if (period === RegisterBusinessNamePeriods.None) {
      await this._updateProduct(CompanyProduct.RegisterBusinessName, false);
      return;
    }

    await this._updateProduct(CompanyProduct.RegisterBusinessName, true, { name, period });
  }

  private async _updateProduct(
    product: CompanyProduct,
    toggle: boolean,
    details?: DefaultCompanyProductDetailsData,
  ): Promise<void> {
    toggle
      ? await this._companyProductsStore.addProduct(product, details)
      : await this._companyProductsStore.deleteProduct(product);
  }

  private async _updatePricingPlan(pricingPlan?: PricingPlanAu): Promise<void> {
    if (!pricingPlan) {
      throw new AnnaError("No pricing plan selected");
    }

    await this._pricingPlanStore.updatePricingPlan(pricingPlan);
  }
}
